import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TermRow, type LineOption, type ProductOption } from "./TermRow";
import { IndicatorValueCard } from "./IndicatorValueCard";
import type {
  IndicatorDraft,
  IndicatorTerm,
  IndicatorValueResponse,
} from "@/types/indicators.types";

interface IndicatorEditorProps {
  draft: IndicatorDraft;
  setDraft: (patch: Partial<IndicatorDraft>) => void;
  lines: LineOption[];
  productsFor: (lineId: string, role: IndicatorTerm["role"]) => ProductOption[];
  value: IndicatorValueResponse | null;
  isCalculating: boolean;
  isSaving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

const VAZIO = (side: IndicatorTerm["side"]): IndicatorTerm => ({
  side,
  lineId: "",
  role: side === "NUMERATOR" ? "PRODUCES" : "CONSUMES",
  productId: "",
});

function Lado({
  side,
  titulo,
  draft,
  setDraft,
  lines,
  productsFor,
}: Pick<IndicatorEditorProps, "draft" | "setDraft" | "lines" | "productsFor"> & {
  side: IndicatorTerm["side"];
  titulo: string;
}) {
  const termos = draft.terms.map((t, i) => ({ t, i })).filter(({ t }) => t.side === side);
  return (
    <div className="space-y-2">
      <h4 className="text-[13px] font-medium text-text-primary">{titulo}</h4>
      {termos.map(({ t, i }) => (
        <TermRow
          key={i}
          term={t}
          lines={lines}
          productsFor={productsFor}
          onChange={(patch) =>
            setDraft({ terms: draft.terms.map((x, j) => (j === i ? { ...x, ...patch } : x)) })
          }
          onRemove={() => setDraft({ terms: draft.terms.filter((_, j) => j !== i) })}
        />
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setDraft({ terms: [...draft.terms, VAZIO(side)] })}
      >
        <Plus size={14} className="mr-1" />
        Nova frase
      </Button>
    </div>
  );
}

/** Spec 082 — o indicador é montado por FRASES, e o número recalcula a cada uma. */
export function IndicatorEditor(props: IndicatorEditorProps) {
  const { draft, setDraft, isSaving, onSave, onCancel } = props;
  const completo =
    draft.name.trim() !== "" &&
    draft.baseUnit.trim() !== "" &&
    draft.terms.some((t) => t.side === "NUMERATOR" && t.productId) &&
    draft.terms.some((t) => t.side === "DENOMINATOR" && t.productId);

  return (
    <div className="space-y-4 rounded-md border border-border-default p-4">
      <div className="grid gap-3 md:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="indicator-name">Nome</Label>
          <Input
            id="indicator-name"
            placeholder="Aproveitamento"
            value={draft.name}
            onChange={(e) => setDraft({ name: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="indicator-unit">Unidade base</Label>
          <Input
            id="indicator-unit"
            placeholder="kg"
            value={draft.baseUnit}
            onChange={(e) => setDraft({ baseUnit: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="indicator-target">Meta (%)</Label>
          <Input
            id="indicator-target"
            type="number"
            placeholder="0"
            value={draft.target ?? ""}
            onChange={(e) =>
              setDraft({ target: e.target.value === "" ? undefined : Number(e.target.value) })
            }
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Lado {...props} side="NUMERATOR" titulo="Entra no numerador" />
        <Lado {...props} side="DENOMINATOR" titulo="Entra no denominador" />
      </div>

      <IndicatorValueCard value={props.value} isCalculating={props.isCalculating} />

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button onClick={onSave} disabled={!completo || isSaving}>
          Salvar indicador
        </Button>
      </div>
    </div>
  );
}
